import { isEmpty } from 'lodash-es';
import { useCallback, useLayoutEffect, useState } from 'react';

import { SearchType } from 'app/constants';
import usePersistent from 'hooks/use-persistent';

import { defaultSearchBarValue } from '../constants';
import { ChangeTrigger, SearchFieldErrors, SearchFieldValue } from '../types';

interface UseSearchFieldProps {
    value?: SearchFieldValue;
    onChange?: (value: SearchFieldValue, trigger: ChangeTrigger) => void;
    onInputFieldEnter?: () => void;
    onSubmit?: () => void;
    autoCompleteInputRef?: React.RefObject<HTMLInputElement>;
}

export const useSearchField = ({
    value,
    onChange,
    onInputFieldEnter,
    onSubmit,
    autoCompleteInputRef
}: UseSearchFieldProps) => {
    const [internalState, setInternalState] = useState<SearchFieldValue>(value ?? defaultSearchBarValue);
    const [errors, setErrors] = useState<SearchFieldErrors>({});

    const persistentInternalState = usePersistent(internalState);
    const { type, query, software, version } = internalState;

    const checkSearchFieldValue = useCallback(() => {
        const { type, software, version } = persistentInternalState.current;
        const errors: SearchFieldErrors = {};
        if (type === SearchType.product) {
            if (!software?.trim()) {
                errors.software = 'Required';
            }
            if (!version?.trim()) {
                errors.version = 'Required';
            }
        }

        setErrors(errors);
        return isEmpty(errors);
    }, [persistentInternalState]);

    const updateValue = useCallback(
        (
            patch: Partial<SearchFieldValue> | ((source: SearchFieldValue) => SearchFieldValue),
            trigger: ChangeTrigger
        ) => {
            let newValue: SearchFieldValue = defaultSearchBarValue;

            setInternalState((source) => {
                newValue = typeof patch === 'function' ? patch(source) : { ...source, ...patch };
                return newValue;
            });

            // Send updates to subscriber
            onChange?.(newValue, trigger);
        },
        [onChange]
    );

    useLayoutEffect(() => {
        // Check if component is controlled
        if (value === undefined || value === null) {
            return;
        }

        // Sync controlled state with props
        setInternalState((state) => {
            for (const key in value) {
                if (state[key] !== value[key]) {
                    return { ...state, ...value };
                }
            }
            return state;
        });
    }, [value]);

    const handleReset = useCallback(() => {
        updateValue(({ type }) => ({ ...defaultSearchBarValue, type }), ChangeTrigger.select);
        setErrors({});
    }, [updateValue]);

    const handleTypeChange = useCallback(
        (type: SearchType) => {
            updateValue((state) => {
                switch (true) {
                    case state.type === SearchType.elastic && type === SearchType.simple:
                        return { ...defaultSearchBarValue, query: state.query, type };
                    default:
                        return { ...defaultSearchBarValue, type };
                }
            }, ChangeTrigger.select);
            setErrors({});
        },
        [updateValue]
    );

    const handleQueryChange = useCallback(
        (query: string, trigger: ChangeTrigger) => updateValue({ query }, trigger),
        [updateValue]
    );

    const handleSoftwareChange = useCallback(
        (software: string, trigger: ChangeTrigger) => {
            updateValue({ software }, trigger);
            setErrors(({ software: _, ...errors }) => errors);
        },
        [updateValue]
    );

    const handleVersionChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            updateValue({ version: event.target.value }, ChangeTrigger.input);
            setErrors(({ version: _, ...errors }) => errors);
        },
        [updateValue]
    );

    const handleAutoCompleteKeyDown: React.KeyboardEventHandler<HTMLInputElement> = useCallback(
        (event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                event.stopPropagation();
                autoCompleteInputRef?.current?.blur();
                if (checkSearchFieldValue()) {
                    onInputFieldEnter?.();
                }
            }
        },
        [autoCompleteInputRef, onInputFieldEnter, checkSearchFieldValue]
    );

    const handleSubmit = useCallback(() => {
        if (checkSearchFieldValue()) {
            onSubmit?.();
        }
    }, [checkSearchFieldValue, onSubmit]);

    const canClear = [query, software, version].some((value) => value.trim());

    return {
        type,
        query,
        software,
        version,
        errors,
        canClear,
        handleReset,
        handleTypeChange,
        handleQueryChange,
        handleSoftwareChange,
        handleVersionChange,
        handleAutoCompleteKeyDown,
        handleSubmit
    };
};
